import type { QueryClient } from '@tanstack/react-query';
import type { Listing } from 'app/types/listing';

import { listingKeys } from './keys';

export function hasListingUser(listing: unknown): listing is Listing {
  return typeof listing === 'object' && listing !== null && 'user' in listing && Boolean((listing as Listing).user);
}

function getMergedCounter(previousValue?: number | null, nextValue?: number | null) {
  if (!Number.isInteger(previousValue) && !Number.isInteger(nextValue)) {
    return undefined;
  }

  return Math.max(previousValue ?? 0, nextValue ?? 0);
}

export function mergeListingDetails(previous: Listing | undefined, next: Listing): Listing {
  if (!previous) {
    return next;
  }

  const numViews = getMergedCounter(previous.numViews, next.numViews);
  const numInteractions = getMergedCounter(previous.numInteractions, next.numInteractions);
  // Unlike views and interactions, favourites can decrease when someone removes a favourite.
  // Always accept the latest server value instead of retaining the highest cached count.
  const numFavourites = next.numFavourites ?? previous.numFavourites;
  const trustPoints = getMergedCounter(previous.user.trustPoints, next.user.trustPoints);

  return {
    ...previous,
    ...next,
    lender: next.lender === undefined ? previous.lender : next.lender,
    lenders: next.lenders === undefined ? previous.lenders : next.lenders,
    additionalLendersCount:
      next.additionalLendersCount === undefined ? previous.additionalLendersCount : next.additionalLendersCount,
    numViews,
    numInteractions,
    numFavourites,
    user: {
      ...previous.user,
      ...next.user,
      trustPoints,
    },
  };
}

export function mergeListingDetailCache(client: QueryClient, listing: Listing) {
  client.setQueryData<Listing>(listingKeys.detail(listing.id), (previous) => {
    if (!hasListingUser(previous)) {
      return listing;
    }

    return mergeListingDetails(previous, listing);
  });
}
